import axios from 'axios';

/**
 * Public OSM Nominatim endpoint. Key-free, and the basemap is already OSM /
 * CartoDB carrying OSM attribution (`styles/themes.ts`), so this introduces no
 * new provider, API key, or `.env` entry.
 *
 * Not routed through `apiClient`: that one carries the operator's Dex id_token
 * and points at our own API. Neither belongs on a third-party request.
 */
const NOMINATIM_SEARCH_URL = 'https://nominatim.openstreetmap.org/search';

/** Give up rather than leave the operator watching a spinner indefinitely. */
const LOOKUP_TIMEOUT_MS = 8000;

export interface GeocodeResult {
  lat: number;
  lng: number;
  /** Provider's name for the match, so the operator can confirm the hit. */
  label: string;
}

/** The subset of a Nominatim place we use. */
interface NominatimPlace {
  lat: string;
  lon: string;
  display_name: string;
}

export const geocodingService = {
  /**
   * Resolve a free-text address or postal code to its single best match.
   *
   * Returns null when the provider has nothing — an ordinary outcome, not a
   * failure. Throws when the lookup itself could not be made (offline, timeout,
   * 5xx), so the caller can tell "nowhere by that name" from "we could not
   * ask" and say so.
   */
  async search(query: string): Promise<GeocodeResult | null> {
    try {
      const { data } = await axios.get<NominatimPlace[]>(NOMINATIM_SEARCH_URL, {
        params: { q: query, format: 'jsonv2', limit: 1 },
        timeout: LOOKUP_TIMEOUT_MS,
      });

      const place = data?.[0];
      if (!place) {
        return null;
      }

      const lat = Number(place.lat);
      const lng = Number(place.lon);
      // A match we cannot put on the map is no match at all.
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return null;
      }

      return { lat, lng, label: place.display_name };
    } catch (error) {
      console.error(`Geocoding lookup failed for "${query}":`, error);
      throw error;
    }
  },
};
